// src/factory.ts
import { createFallbackMonitoringClient } from "@wix/monitoring";

// src/utils.ts
import { generateTagsAndContext } from "@wix/monitoring";
var setTagsAndContextsOnScope = (scope, { tags, contexts }) => {
  if (tags) {
    scope.setTags(tags);
  }
  if (contexts) {
    Object.entries(contexts).forEach(
      ([key, value]) => typeof value !== "undefined" && scope.setContext(key, value)
    );
  }
};
var setHostContextOnScope = (scope, hostContext) => {
  if (!hostContext) {
    return;
  }
  const { tags, contexts } = generateTagsAndContext(hostContext);
  setTagsAndContextsOnScope(scope, { tags, contexts });
};

// src/micro-frontend/utils.ts
var withCaptureScope = (client, scope, sentry, captureContext = {}, defaultLevel = "info", callback) => {
  const hub = new sentry.Hub(client, scope);
  return hub.withScope((_scope) => {
    const { level = defaultLevel, tags, contexts } = captureContext;
    _scope.setLevel(level);
    setTagsAndContextsOnScope(_scope, { tags, contexts });
    return callback(_scope, hub);
  });
};

// src/abstract-browser-client.ts
var AbstractSentryBrowserClient = class {
  constructor(options) {
    this.sentry = options.sentrySDK;
    this.scope = new this.sentry.Scope();
    this.sentryClient = new this.sentry.BrowserClient({
      dsn: options.dsn,
      transport: options.transport ?? this.sentry.makeFetchTransport,
      integrations: this.sentry.getDefaultIntegrations({}),
      stackParser: this.sentry.defaultStackParser,
      tracesSampleRate: options.tracesSampleRate || 1,
      debug: options.debug ?? false,
      environment: options.environment,
      release: options.release
    });
    setHostContextOnScope(this.scope, options.hostContext);
    this.scope.setClient(this.sentryClient);
    this.sentryClient.init();
  }
};

// src/micro-frontend/client.ts
var SentryBrowserMicroFrontendClient = class extends AbstractSentryBrowserClient {
  constructor() {
    super(...arguments);
    /**
     * Records a new breadcrumb which will be attached to future events.
     * Breadcrumbs will be added to subsequent events to provide more context on user's actions prior to an error or crash.
     * @param breadcrumb The breadcrumb to record.
     */
    this.addBreadcrumb = (breadcrumb) => {
      const filteredBreadcrumb = {
        type: breadcrumb.type,
        category: breadcrumb.category,
        message: breadcrumb.message,
        level: breadcrumb.level,
        data: breadcrumb.data
      };
      this.scope.addBreadcrumb(filteredBreadcrumb);
    };
    /**
     * Captures an exception event and sends it to Sentry.
     * @param error The error to capture
     * @param captureContext Optional additional data to attach to the Sentry e vent.
     * @returns the id of the captured Sentry event.
     */
    this.captureException = (error, captureContext) => {
      return withCaptureScope(
        this.sentryClient,
        this.scope,
        this.sentry,
        captureContext,
        "error",
        (scope) => scope.captureException(error)
      );
    };
    /**
     * Captures a message event and sends it to Sentry.
     * @param message The message to capture
     * @param captureContext Define the level of the message or pass in additional data to attach to the message.
     * @returns the id of the captured message.
     */
    this.captureMessage = (message, captureContext) => {
      return withCaptureScope(
        this.sentryClient,
        this.scope,
        this.sentry,
        captureContext,
        "info",
        (scope) => scope.captureMessage(message)
      );
    };
    /**
     * Wraps a function with a span and finishes the span after the function is done. The created span is the active span and will be used as parent by other spans created inside the function, as long as the function is executed while the scope is active.
     * @param spanOptions The options for the span
     * @param callback The function to wrap with a span
     * @returns The return value of the callback
     */
    this.startSpan = (spanOptions, callback) => {
      const filteredSpanOptions = {
        name: spanOptions.name,
        tags: spanOptions.tags
      };
      return this.sentry.startSpan(
        { ...filteredSpanOptions, scope: this.scope },
        callback
      );
    };
  }
};

// src/browser/client.ts
var SentryBrowserClient = class extends AbstractSentryBrowserClient {
  constructor() {
    super(...arguments);
    /**
     * Records a new breadcrumb which will be attached to future events.
     * Breadcrumbs will be added to subsequent events to provide more context on user's actions prior to an error or crash.
     * @param breadcrumb The breadcrumb to record.
     */
    this.addBreadcrumb = (breadcrumb) => {
      const filteredBreadcrumb = {
        type: breadcrumb.type,
        category: breadcrumb.category,
        message: breadcrumb.message,
        level: breadcrumb.level,
        data: breadcrumb.data
      };
      this.scope.addBreadcrumb(filteredBreadcrumb);
    };
    /**
     * Captures an exception event and sends it to Sentry.
     * @param error The error to capture
     * @param captureContext Optional additional data to attach to the Sentry e vent.
     * @returns the id of the captured Sentry event.
     */
    this.captureException = (error, captureContext) => {
      return this.sentryClient.captureException(
        error,
        {
          captureContext: {
            level: captureContext?.level ?? "error",
            tags: captureContext?.tags,
            contexts: captureContext?.contexts
          }
        },
        this.scope
      );
    };
    /**
     * Captures a message event and sends it to Sentry.
     * @param message The message to capture
     * @param captureContext Define the level of the message or pass in additional data to attach to the message.
     * @returns the id of the captured message.
     */
    this.captureMessage = (message, captureContext) => {
      return this.sentryClient.captureMessage(
        message,
        captureContext?.level ?? "error",
        {
          captureContext: {
            level: captureContext?.level ?? "error",
            tags: captureContext?.tags,
            contexts: captureContext?.contexts
          }
        },
        this.scope
      );
    };
    /**
     * Wraps a function with a span and finishes the span after the function is done. The created span is the active span and will be used as parent by other spans created inside the function, as long as the function is executed while the scope is active.
     * @param spanOptions The options for the span
     * @param callback The function to wrap with a span
     * @returns The return value of the callback
     */
    this.startSpan = (spanOptions, callback) => {
      const filteredSpanOptions = {
        name: spanOptions.name,
        tags: spanOptions.tags
      };
      return this.sentry.startSpan(
        { ...filteredSpanOptions, scope: this.scope },
        callback
      );
    };
  }
};

// src/factory.ts
var createSentryBrowserClient = ({
  microFrontendEnvironment,
  sentrySDK,
  dsn,
  ...options
}) => {
  if (!sentrySDK) {
    return createFallbackMonitoringClient(
      "Unsupported environment - Sentry SDK is not loaded into the environment. Please make sure to use monitoring SDK only in supported environments."
    );
  }
  if (!dsn) {
    return createFallbackMonitoringClient(
      "Missing Sentry DSN in the app's monitoring configuration. Please make sure to set it."
    );
  }
  if (microFrontendEnvironment) {
    return new SentryBrowserMicroFrontendClient({ ...options, dsn, sentrySDK });
  }
  return new SentryBrowserClient({ ...options, dsn, sentrySDK });
};
export {
  createSentryBrowserClient
};
